/**
 * AuthorExportService — read the entire self-author tree from Firestore and
 * package it as a single JSON envelope the user can download and re-import
 * into another project via AuthorImportService.
 *
 * Envelope shape mirrors StrategyExportService:
 *   {
 *     __segmently_self_author_export__: true,
 *     version: '1.0.0',
 *     type: 'self-author',
 *     payload: { author, platforms[], topicPillars[], creatorReference },
 *     metadata: { exportedAt, sourceProjectId, authorId, displayName, counts }
 *   }
 *
 * The discriminator `__segmently_self_author_export__: true` is used by
 * AuthorImportService.validate() to reject foreign / corrupted JSON.
 *
 * Reads are parallelised aggressively (Promise.all over platforms × 5
 * subcollections) — for a creator with several platforms × 200 posts, this finishes
 * in seconds because every subcollection read is independent.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { authorFirestoreService, type AuthorDoc } from './AuthorFirestoreService';
import type { Platform, CreatorReference } from '@segmently/schema-models/content-plan';
import type { PlatformVoice, Post, PerAuthorPostTemplate } from '../types';
import type { TopicPillarLite } from '../hooks/usePillars';

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

/** PostFormat doc shape — copied from system_platforms during onboarding.
 *  No frontend service touches this collection so the type is loose; on
 *  round-trip we preserve the doc verbatim (sans Firestore sentinels). */
export type PostFormatDoc = Record<string, unknown> & { id?: string };

/** Lite shape for the legacy creator_references self-ref doc. */
export type CreatorReferenceDoc = CreatorReference & Record<string, unknown>;

export interface ExportedPlatformBundle {
  platform: Platform & Record<string, unknown>;
  voice: PlatformVoice | null;
  designDocs: PostFormatDoc[];
  designProfileSets: Array<{
    set: Record<string, unknown> & { id: string };
    profiles: Array<(Record<string, unknown> & { id: string }) & {
      references: Array<Record<string, unknown> & { id: string }>;
    }>;
  }>;
  formats: PostFormatDoc[];
  postExamples: Array<Record<string, unknown> & { id: string }>;
  postTemplates: Array<PerAuthorPostTemplate & { id: string }>;
  authorPosts: Post[];
}

export interface SelfAuthorExportPayload {
  author: AuthorDoc;
  platforms: ExportedPlatformBundle[];
  topicPillars: TopicPillarLite[];
  creatorReference: CreatorReferenceDoc | null;
}

export interface SelfAuthorExportEnvelope {
  __segmently_self_author_export__: true;
  version: '1.0.0';
  type: 'self-author';
  payload: SelfAuthorExportPayload;
  metadata: {
    exportedAt: string;
    sourceProjectId: string;
    authorId: string;
    displayName: string;
    counts: {
      platforms: number;
      authorPosts: number;
      pillars: number;
      templates: number;
      examples: number;
      formats: number;
      designDocs: number;
      designProfileSets: number;
      designProfiles: number;
      designReferences: number;
    };
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────────────────────────

export class AuthorExportService {
  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  /**
   * Build a SelfAuthorExportEnvelope for a self-author. When no authorId is
   * passed, export the project's baseline default author.
   * Throws when no default author exists — caller should gate the Export
   * button on `useAuthor().author !== null`.
   */
  async exportSelfAuthor(projectId: string, authorId?: string | null): Promise<SelfAuthorExportEnvelope> {
    const author = authorId
      ? await authorFirestoreService.getAuthorById(projectId, authorId)
      : await authorFirestoreService.getDefaultAuthor(projectId);
    if (!author) {
      throw new Error(authorId
        ? `Author ${authorId} not found in project ${projectId}`
        : `No default self-author found in project ${projectId}`);
    }
    const resolvedAuthorId = author.id;
    if (!resolvedAuthorId) {
      throw new Error(`Default author in project ${projectId} has no id field`);
    }

    // Fetch platforms + pillars + creator_reference in parallel.
    const platformsCol = collection(
      this.db, 'projects', projectId, 'authors', resolvedAuthorId, 'platforms',
    );
    const pillarsQuery = query(
      collection(this.db, 'projects', projectId, 'topic_pillars'),
      where('authorIds', 'array-contains', resolvedAuthorId),
    );
    const crefRef = doc(
      this.db, 'projects', projectId, 'creator_references', `cref_self_${resolvedAuthorId}`,
    );

    const [platformsSnap, pillarsSnap, crefSnap] = await Promise.all([
      getDocs(platformsCol),
      getDocs(pillarsQuery),
      getDoc(crefRef),
    ]);

    // For each platform, fan out to its author-scoped subcollections.
    const platforms: ExportedPlatformBundle[] = await Promise.all(
      platformsSnap.docs.map((platformDoc) =>
        this.readPlatformBundle(projectId, resolvedAuthorId, platformDoc.id, platformDoc.data() as Platform),
      ),
    );

    const topicPillars: TopicPillarLite[] = pillarsSnap.docs.map((d) => ({
      ...(d.data() as Partial<TopicPillarLite>),
      id: d.id,
    } as TopicPillarLite));

    const creatorReference: CreatorReferenceDoc | null = crefSnap.exists()
      ? (crefSnap.data() as CreatorReferenceDoc)
      : null;

    const payload: SelfAuthorExportPayload = {
      author,
      platforms,
      topicPillars,
      creatorReference,
    };

    const counts = {
      platforms: platforms.length,
      authorPosts: platforms.reduce((sum, p) => sum + p.authorPosts.length, 0),
      pillars: topicPillars.length,
      templates: platforms.reduce((sum, p) => sum + p.postTemplates.length, 0),
      examples: platforms.reduce((sum, p) => sum + p.postExamples.length, 0),
      formats: platforms.reduce((sum, p) => sum + p.formats.length, 0),
      designDocs: platforms.reduce((sum, p) => sum + p.designDocs.length, 0),
      designProfileSets: platforms.reduce((sum, p) => sum + p.designProfileSets.length, 0),
      designProfiles: platforms.reduce(
        (sum, p) => sum + p.designProfileSets.reduce((inner, set) => inner + set.profiles.length, 0),
        0,
      ),
      designReferences: platforms.reduce(
        (sum, p) => sum + p.designProfileSets.reduce(
          (inner, set) => inner + set.profiles.reduce((profileSum, profile) => profileSum + profile.references.length, 0),
          0,
        ),
        0,
      ),
    };

    return {
      __segmently_self_author_export__: true,
      version: '1.0.0',
      type: 'self-author',
      payload,
      metadata: {
        exportedAt: new Date().toISOString(),
        sourceProjectId: projectId,
        authorId: resolvedAuthorId,
        displayName: author.displayName ?? 'Unknown',
        counts,
      },
    };
  }

  private async readPlatformBundle(
    projectId: string,
    authorId: string,
    platformId: string,
    platform: Platform,
  ): Promise<ExportedPlatformBundle> {
    const platformPath = ['projects', projectId, 'authors', authorId, 'platforms', platformId] as const;

    const voiceCol = collection(this.db, ...platformPath, 'voice');
    const designCol = collection(this.db, ...platformPath, 'design');
    const designProfileSetsCol = collection(this.db, ...platformPath, 'design_profile_sets');
    const formatsCol = collection(this.db, ...platformPath, 'formats');
    const postExamplesCol = collection(this.db, ...platformPath, 'post_examples');
    const postTemplatesCol = collection(this.db, ...platformPath, 'post_templates');
    const authorPostsCol = collection(this.db, ...platformPath, 'author_posts');

    const [voiceSnap, designSnap, designProfileSetsSnap, formatsSnap, examplesSnap, templatesSnap, postsSnap] = await Promise.all([
      getDocs(voiceCol),
      getDocs(designCol),
      getDocs(designProfileSetsCol),
      getDocs(formatsCol),
      getDocs(postExamplesCol),
      getDocs(postTemplatesCol),
      getDocs(authorPostsCol),
    ]);

    // voice/voice is a single-doc subcollection. If the doc with id 'voice'
    // exists, take it; otherwise take whatever first doc is present (some
    // legacy authors may have written a different id).
    let voice: PlatformVoice | null = null;
    if (!voiceSnap.empty) {
      const voiceDoc = voiceSnap.docs.find((d) => d.id === 'voice') ?? voiceSnap.docs[0];
      voice = voiceDoc!.data() as PlatformVoice;
    }

    const designProfileSets = await Promise.all(
      designProfileSetsSnap.docs.map(async (setDoc) => {
        const profilesSnap = await getDocs(collection(setDoc.ref, 'profiles'));
        const profiles = await Promise.all(
          profilesSnap.docs.map(async (profileDoc) => {
            const referencesSnap = await getDocs(collection(profileDoc.ref, 'references'));
            return {
              ...profileDoc.data(),
              id: profileDoc.id,
              references: referencesSnap.docs.map((refDoc) => ({ ...refDoc.data(), id: refDoc.id })),
            };
          }),
        );
        return {
          set: { ...setDoc.data(), id: setDoc.id },
          profiles,
        };
      }),
    );

    return {
      platform: { ...platform, id: platform.id ?? platformId } as Platform & Record<string, unknown>,
      voice,
      designDocs: designSnap.docs.map((d) => ({ ...d.data(), id: d.id })) as PostFormatDoc[],
      designProfileSets,
      formats: formatsSnap.docs.map((d) => ({ ...d.data(), id: d.id })) as PostFormatDoc[],
      postExamples: examplesSnap.docs.map((d) => ({ ...d.data(), id: d.id })),
      postTemplates: templatesSnap.docs.map((d) => ({ ...d.data(), id: d.id })) as Array<PerAuthorPostTemplate & { id: string }>,
      authorPosts: postsSnap.docs.map((d) => ({ ...d.data(), id: d.id })) as Post[],
    };
  }

  /**
   * Trigger a browser download of the JSON envelope.
   * Lifted verbatim from StrategyExportService.download — same pattern.
   */
  static download(data: unknown, filename: string): void {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Build a stable filename for the export. Slugifies the displayName and
   * appends today's date so multiple exports don't collide on disk.
   */
  static buildFilename(displayName: string): string {
    const slug = displayName
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')      // strip diacritics
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'creator';
    const date = new Date().toISOString().slice(0, 10);
    return `creator-${slug}-${date}.json`;
  }
}

export const authorExportService = new AuthorExportService();
